import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, Clock, Loader2, ShieldCheck, XCircle, Paperclip, MessageSquare, Send, User } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusStyle = (s) => {
  const v = (s || '').toLowerCase();
  if (v === 'closed') return 'bg-primary text-primary-foreground';
  if (v === 'in_progress') return 'bg-blue-500/20 text-blue-500 border border-blue-500/40';
  if (v === 'waiting_on_client') return 'bg-amber-500/20 text-amber-500 border border-amber-500/40';
  return 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/40';
};

const priorityStyle = (p) => {
  const v = (p || '').toLowerCase();
  if (v === 'urgent') return 'border-red-500/50 text-red-500';
  if (v === 'high') return 'border-orange-500/50 text-orange-500';
  if (v === 'medium') return 'border-yellow-500/50 text-yellow-500';
  return 'border-blue-500/50 text-blue-500';
};

/**
 * Public read-only ticket-status page for anonymous subscribers.
 * Auto-polls every 30s so visitors who keep the tab open see admin replies
 * the moment they land.
 */
const PublicTicket = () => {
  const { token } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reply form state
  const [replyName, setReplyName] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchTicket = async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/public/tickets/${token}`);
        if (!cancelled) {
          setTicket(r.data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.status === 404 ? 'Ticket link not found' : 'Failed to load ticket');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTicket();
    const id = setInterval(fetchTicket, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;
    setPosting(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/public/tickets/${token}/reply`,
        { message: replyMessage.trim(), name: replyName.trim() || undefined },
      );
      setTicket(r.data.ticket);
      setReplyMessage('');
      toast.success('Reply posted — our team has been notified');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to post reply';
      toast.error(detail);
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground font-mono uppercase tracking-widest text-xs">Loading ticket…</p>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md p-8 text-center border-border" data-testid="public-ticket-error">
          <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h1 className="text-2xl font-black mb-2">Link not found</h1>
          <p className="text-muted-foreground">{error || 'The ticket you are looking for no longer exists.'}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-ticket-page">
      <header className="border-b border-border bg-card/50 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <div className="text-2xl font-black">
              NEXT<span className="text-primary">/</span>GEN
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-1">
              Network Solutions
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <span className="hidden sm:inline">Secure status link</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Support Ticket</p>
            <h1 className="text-2xl sm:text-3xl font-black" data-testid="public-ticket-title">{ticket.title}</h1>
            <p className="text-xs font-mono text-muted-foreground mt-2">{ticket.ticket_id}</p>
          </div>
          <div className="text-right space-y-2">
            <Badge className={`${statusStyle(ticket.status)} text-sm`} data-testid="public-ticket-status">
              {(ticket.status || 'open').replace('_', ' ').toUpperCase()}
            </Badge>
            <Badge variant="outline" className={`${priorityStyle(ticket.priority)} text-xs ml-2`}>
              {(ticket.priority || 'medium').toUpperCase()}
            </Badge>
          </div>
        </div>

        {/* Subscriber info */}
        {ticket.subscriber_email && (
          <Card className="border-border p-4 mb-6">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Reply Address</p>
            <p className="text-sm">
              {ticket.subscriber_name && <span className="font-medium">{ticket.subscriber_name} </span>}
              <span className="text-muted-foreground">&lt;{ticket.subscriber_email}&gt;</span>
            </p>
          </Card>
        )}

        {/* Conversation thread — original message + comments + reply form */}
        <Card className="border-border p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-4 h-4 text-muted-foreground" />
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Conversation</p>
          </div>

          {/* Original message bubble */}
          <div className="mb-4 pb-4 border-b border-border">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-medium">{ticket.subscriber_name || 'You'}</span>
              <span className="text-[10px] text-muted-foreground">
                {ticket.created_at && format(new Date(ticket.created_at), 'MMM dd · h:mm a')}
              </span>
            </div>
            <p className="text-sm whitespace-pre-wrap" data-testid="public-ticket-description">{ticket.description}</p>
          </div>

          {/* Threaded comments — staff and subscriber replies */}
          {(ticket.comments || []).map((c, i) => {
            const isStaff = c.author_kind === 'staff';
            return (
              <div
                key={i}
                className={`mb-3 p-3 rounded-md ${isStaff ? 'bg-primary/5 border border-primary/30' : 'bg-muted/30 border border-border'}`}
                data-testid={`public-ticket-comment-${i}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {isStaff ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                  ) : (
                    <User className="w-3.5 h-3.5 text-muted-foreground" />
                  )}
                  <span className={`text-xs font-medium ${isStaff ? 'text-primary' : ''}`}>
                    {isStaff ? `${c.author_name || 'NextGen Team'}` : (c.author_name || 'You')}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {c.created_at && format(new Date(c.created_at), 'MMM dd · h:mm a')}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{c.message}</p>
              </div>
            );
          })}

          {/* Fallback: if no comments[] yet but admin_response is set (old tickets), show it */}
          {(!ticket.comments || ticket.comments.length === 0) && ticket.admin_response && (
            <div className="mb-3 p-3 rounded-md bg-primary/5 border border-primary/30" data-testid="public-ticket-reply">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-medium text-primary">NextGen Team</span>
                <span className="text-[10px] text-muted-foreground">
                  {ticket.updated_at && format(new Date(ticket.updated_at), 'MMM dd · h:mm a')}
                </span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{ticket.admin_response}</p>
            </div>
          )}

          {(!ticket.comments || ticket.comments.length === 0) && !ticket.admin_response && (
            <div className="text-center py-6 mb-3 text-xs text-muted-foreground" data-testid="public-ticket-pending">
              <Clock className="w-6 h-6 mx-auto mb-2 opacity-60" />
              No reply yet — auto-refreshing every 30 seconds.
            </div>
          )}

          {/* Reply form — hidden when ticket is closed */}
          {(ticket.status || '').toLowerCase() !== 'closed' ? (
            <form onSubmit={handleReply} className="mt-4 pt-4 border-t border-border space-y-3" data-testid="public-ticket-reply-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Your name (optional)</Label>
                  <Input
                    value={replyName}
                    onChange={(e) => setReplyName(e.target.value)}
                    placeholder={ticket.subscriber_name || 'Your name'}
                    className="bg-input"
                    data-testid="public-ticket-reply-name"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Your reply</Label>
                <Textarea
                  required
                  rows={4}
                  maxLength={5000}
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Add a follow-up question or update…"
                  className="bg-input"
                  data-testid="public-ticket-reply-message"
                />
              </div>
              <Button
                type="submit"
                disabled={posting || !replyMessage.trim()}
                className="font-bold uppercase tracking-widest"
                data-testid="public-ticket-reply-submit"
              >
                {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {posting ? 'Posting…' : 'Post Reply'}
              </Button>
            </form>
          ) : (
            <p className="text-xs text-muted-foreground italic mt-4 pt-4 border-t border-border">
              This ticket is closed. Need more help? Email or call <a href="tel:7403025607" className="text-primary">740-302-5607</a>.
            </p>
          )}
        </Card>

        {/* Attachments (count only — files not exposed publicly) */}
        {ticket.attachments && ticket.attachments.length > 0 && (
          <Card className="border-border p-4 mb-6">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
              <Paperclip className="w-3 h-3" />
              {ticket.attachments.length} attachment(s)
            </p>
            <ul className="space-y-1">
              {ticket.attachments.map((a, i) => (
                <li key={i} className="text-xs font-mono text-muted-foreground flex items-center gap-2">
                  <span className="text-foreground">{a.filename}</span>
                  <span>· {Math.round((a.size || 0) / 1024)} KB</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <footer className="mt-12 text-center text-xs text-muted-foreground space-y-1">
          <p>Need a faster reply? Email or call <a href="tel:7403025607" className="text-primary">740-302-5607</a>.</p>
          <p>&copy; {new Date().getFullYear()} NextGen Network Solutions</p>
        </footer>
      </main>
    </div>
  );
};

export default PublicTicket;
